/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Ayi Regiarty
*/

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { formatISO, startOfDay } from 'date-fns';
import SociollaText from '@components/sociolla/SociollaText';
import ItemDetail from '@components/global/ItemDetail';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import * as Layout from '@components/screen/Layout';
import { isWeb, isWebPlatform, isMobileWeb } from '@global/screens';
import {
  hasNoAccess,
  parseDate,
  downloadFile,
  getFileNameFromUrl,
} from '@global/helpers';
import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import StatusBadge from '@components/global/StatusBadge';
import FileInput from '@components/form/FileInput';
import AttachmentsInput from '@components/form/AttachmentsInput';
import DatePicker from '@components/form/DatePicker';
import Image from '@components/global/Image';
import ScrollContainer from '@components/global/ScrollContainer';
import { showAlert, setTempData } from '@/store/global';
import { showPopup } from '@/store/modal';
import Toast from 'react-native-toast-message';
import ModalReject from '@components/customer/ModalReject';
import ModalLog from '@components/modal/ModalLog';
import { LPB_LOG_FORMATTER } from '@/constants/log';
import { NET_DOMAIN } from '@/constants';
import FloatCommentButton from '@components/comment/FloatCommentButton';
import CommentDrawer from '@components/comment/CommentDrawer';

const LPBDetailScreen = (props) => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.auth?.user);
  const userRole = useSelector((state) => state.auth?.user?.role?.code);

  // No comments for 3PL users or on the external (NET) web domain.
  const hideComments =
    userRole === '3pl' ||
    (isWebPlatform && NET_DOMAIN.includes(window.location.hostname));

  const [detailData, setDetailData] = useState({});
  const [showComments, setShowComments] = useState(
    props.route.params?.openComments === 'true',
  );
  useState(false);
  const [modalReject, setModalReject] = useState(false);
  const [showModalLog, setShowModalLog] = useState(false);
  const [savingDocDate, setSavingDocDate] = useState(false);

  const get3plLpbDetail = useApi(MsOrder.get3plLpbDetail);
  const insets = useSafeAreaInsets();
  // const getSkpExternalsBrandBudget = useApi(MsOrder.getSkpExternalsBrandBudget);
  const img = require('@/assets/img/default/user-image.png');

  const restrictedRoles =
    detailData?.status === 'approved' || detailData?.status === 'completed'
      ? ['superadmin', 'spv_logistic', 'general']
      : ['superadmin', '3pl', 'spv_logistic', 'general'];

  const showEditButton = !hasNoAccess(restrictedRoles);
  const showCompletedButton = !hasNoAccess([
    'superadmin',
    'vice_president_finance',
    'budget_controller',
    'finance_head',
    'tax_support',
    'billing_supervisor',
    'general',
  ]);

  const canApprove = () => {
    if (detailData?.status == 'need_approval' && detailData?.current_approval) {
      if (detailData?.current_approval === userRole) {
        const userSalesTeams = (userData?.sales_team || []).map((st) =>
          st.id?.toString(),
        );
        if (userSalesTeams && userSalesTeams.length > 0) {
          return Boolean(
            userSalesTeams.includes(
              detailData?.sales_team_internal_id.toString(),
            ),
          );
        } else {
          return true;
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  const canEdit =
    ['draft', 'rejected', 'need_approval', 'approved', 'completed'].includes(
      detailData?.status,
    ) && showEditButton;

  const canRequestConfirmation =
    ['draft', 'rejected'].includes(detailData?.status) && showEditButton;

  const canUpload =
    ['draft', 'rejected', 'need_approval', 'approved', 'completed'].includes(
      detailData?.status,
    ) && showEditButton;

  // On mobile web the sticky button bar is position: fixed over the scroll area, so the
  // content must end above it. Same height Layout.ButtonSticky renders: 15 top padding +
  // 36 button + 15 button margin + 10 + 12 bottom padding, plus the safe-area inset.
  const hasStickyButton =
    canRequestConfirmation ||
    (showCompletedButton && detailData?.status == 'approved') ||
    canApprove();
  const stickyClearance =
    isMobileWeb && hasStickyButton
      ? { paddingBottom: 88 + insets.bottom }
      : undefined;

  // Document received date is editable in all states (per PRD)
  const canEditDocDate = !hasNoAccess([
    'superadmin',
    'spv_logistic',
    'sales_admin',
    'general',
  ]);

  const getDetail = async (id) => {
    const response = await get3plLpbDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      result.id = result._id;
      dispatch(setTempData(result.status));
      setDetailData(result);
    }
  };

  const saveDocumentReceivedDate = async (value) => {
    if (!value) {
      return;
    }
    try {
      setSavingDocDate(true);
      await MsOrder.update3plLpb(props.route.params.id, {
        document_received_date: formatISO(startOfDay(value)),
      });
      Toast.show({ text1: 'Document received date saved', type: 'success' });
      await getDetail(props.route.params.id);
    } catch (error) {
      dispatch(
        showAlert({
          show: true,
          title: 'Error',
          description:
            error?.response?.data?.message || 'A system error occurred',
          isConfirm: false,
        }),
      );
    } finally {
      setSavingDocDate(false);
    }
  };

  const changeStateToRejected = (reason) => {
    changeStatus(detailData?._id, 'rejected', reason);
  };

  const changeStatus = async (id, status, reason) => {
    try {
      if (id) {
        dispatch(showPopup({ name: 'isLoading', value: true }));
        const payload = {};
        if (reason && status == 'rejected') {
          payload.reason = reason;
        }
        const requestConfirm = await MsOrder.changeStatus3plLpb(
          id,
          status,
          payload,
        );
        if (requestConfirm && requestConfirm.data && requestConfirm.data.data) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          let title = '';
          let desc = '';
          if (status == 'need_approval') {
            title = 'GRN Submitted';
            desc = 'GRN submitted and need confirmation';
          } else if (status == 'approved') {
            title = 'GRN Confirmed';
            desc = 'GRN has been confirmed';
          } else if (status == 'completed') {
            title = 'GRN Confirmed';
            desc = 'GRN has been completed';
          } else if (status == 'draft') {
            title = 'GRN Submitted';
            desc = 'GRN has been drafted';
          } else if (status == 'rejected') {
            title = 'GRN Rejected';
            desc = 'GRN has been rejected';
          }
          setModalReject(false);
          dispatch(
            showAlert({
              show: true,
              title: title,
              description: desc,
              isConfirm: false,
              onCallback: () => {
                getDetail(props.route.params.id);
              },
            }),
          );
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({ text1: 'A system error occurred', type: 'error' });
        }
      }
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      dispatch(
        showAlert({
          show: true,
          title: 'Error',
          description:
            error?.response?.data?.message || 'A system error occurred',
          isConfirm: false,
        }),
      );
    }
    setModalReject(false);
  };

  const deleteFile = (id, value) => {
    dispatch(
      showAlert({
        show: true,
        title: 'Remove Document',
        description: 'This document will be deleted. Do you want to proceed?',
        onCallback: async () => {
          await MsOrder.delete3plLpbDocument(id, value);
          setDetailData({ ...detailData, [value]: '' });
        },
      }),
    );
  };

  const saveFile = async (id, payload) => {
    if (payload.url) {
      await MsOrder.update3plLpbDocument(id, payload);
      getDetail(id);
    }
  };

  const saveOtherFile = async (id, url, name) => {
    await MsOrder.updateLpbAttachment(id, { attachment: { url, name } });
    getDetail(id);
  };

  const deleteOtherFile = async (id, url) => {
    await MsOrder.deleteLpbAttachmentByUrl(id, { url });
    getDetail(id);
  };

  const from = props?.route?.params?.name;
  const goBack = () => {
    if (from === 'GRN Approval') {
      props.navigation.navigate('TaskNotificationNavigator', {
        screen: 'TaskNotificationScreen',
        params: {
          task: true,
        },
      });
    } else {
      props.navigation.replace('LPBDetailScreen', {});
      props.navigation.navigate('LPBScreen');
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      dispatch(setTempData(null));
      setDetailData({});
      if (props?.route?.params?.id) {
        getDetail(props.route.params.id);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  // The mobile header's comment button lives in LPBNavigator.js. Bridge its press back
  // into this screen's drawer state via the route param it sets.
  useEffect(() => {
    if (props.route.params?.showComments) {
      setShowComments(true);
      props.navigation.setParams({ showComments: undefined });
    }
  }, [props.route.params?.showComments]);

  // condition for request confirmation button
  const isAvailableSubmit = () => {
    // Delivery Order and Proforma Invoice are optional for Vietnam GRNs. Uses the record's
    // own country, not the app's current one.
    const isVnLpb = (detailData?.is_active_for || []).includes('b2b_vn');
    const requiredFields = Boolean(
      detailData.pick_up_date &&
        detailData.lpb_number &&
        detailData.shipping_date &&
        detailData.recieved_date &&
        (isVnLpb ||
          (detailData.delivery_order_url && detailData.performa_invoice)),
    );
    if (detailData?.channel?.toLowerCase() == 'mt') {
      return Boolean(requiredFields && detailData.goods_recieved);
    }
    return requiredFields;
  };

  const User = (userProps) => {
    return (
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          justifyContent: 'flex-start',
          ...userProps.style,
        }}
        key={'list-users-' + userProps.index}>
        <Image
          style={{ width: 20, height: 20, marginRight: 8 }}
          width={20}
          height={20}
          isResolveAsset
          source={userProps.image ? userProps.image : img}
        />
        <SociollaText
          style={{ fontSize: 12, color: Colors.blue3, alignSelf: 'center' }}>
          {userProps.name}
        </SociollaText>
      </View>
    );
  };

  return (
    <Layout.Container>
      {get3plLpbDetail?.loading ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : detailData && Object.keys(detailData).length ? (
        <>
          {isWeb ? (
            <Layout.Wrapper white={false}>
              <Layout.Back
                title={from === 'GRN Approval' ? 'Task' : 'GRN'}
                onCallback={() => goBack()}
              />
            </Layout.Wrapper>
          ) : (
            false
          )}
          <ScrollContainer contentContainerStyle={stickyClearance}>
            <Layout.Wrapper rounded>
              <View style={styles.sectionWrapper}>
                {isWeb ? (
                  <View
                    style={[
                      styles.rowContainer,
                      {
                        position: 'relative',
                        zIndex: 1,
                        justifyContent: 'space-between',
                      },
                    ]}>
                    <SociollaText type="medium" style={{ fontSize: 20 }}>
                      {detailData?.do_number || ''}
                    </SociollaText>
                    <View style={{ flexDirection: 'row' }}>
                      {canRequestConfirmation ? (
                        <View style={{ marginRight: 10 }}>
                          <Button
                            type="primary"
                            rounded
                            buttonStyle={{
                              width: 185,
                              marginRight: 10,
                              backgroundColor: Colors.blue0,
                            }}
                            disabled={!isAvailableSubmit()}
                            title={
                              <SociollaText
                                type="bold"
                                style={{ color: Colors.white }}>
                                Request Confirmation
                              </SociollaText>
                            }
                            onPress={() =>
                              changeStatus(detailData?._id, 'need_approval')
                            }
                          />
                        </View>
                      ) : (
                        false
                      )}
                      {showEditButton &&
                      detailData?.status != 'need_approval' &&
                      detailData?.status != 'approved' &&
                      detailData?.status != 'cancelled' &&
                      detailData?.status != 'completed' ? (
                        <Button
                          type="primary"
                          rounded
                          buttonStyle={{ width: 80, marginRight: 10 }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{ color: Colors.white }}>
                              Edit
                            </SociollaText>
                          }
                          onPress={() =>
                            props.navigation.replace('LPBFormScreen', {
                              id: detailData._id,
                              name: detailData.name
                                ? detailData.reference_code
                                : '-',
                              titleScreen: detailData.reference_code
                                ? detailData.reference_code
                                : '-',
                            })
                          }
                        />
                      ) : (
                        false
                      )}
                    </View>
                    {canApprove() ? (
                      <View style={{ flexDirection: 'row' }}>
                        <Button
                          type="primary"
                          rounded
                          buttonStyle={{
                            width: 150,
                            marginRight: 10,
                            backgroundColor: Colors.white,
                          }}
                          title={
                            <SociollaText type="bold" style={{}}>
                              Reject
                            </SociollaText>
                          }
                          onPress={() => setModalReject(true)}
                        />
                        <Button
                          type="primary"
                          rounded
                          buttonStyle={{
                            width: 150,
                            marginRight: 10,
                            backgroundColor: Colors.blue0,
                          }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{ color: Colors.white }}>
                              Approve
                            </SociollaText>
                          }
                          onPress={() =>
                            changeStatus(detailData?._id, 'approved')
                          }
                        />
                      </View>
                    ) : (
                      false
                    )}
                    {showCompletedButton && detailData?.status == 'approved' ? (
                      <View>
                        <Button
                          type="primary"
                          buttonStyle={{
                            width: 120,
                            marginRight: 10,
                            backgroundColor: Colors.blue0,
                            border: 'none',
                          }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{ color: Colors.white }}>
                              Completed
                            </SociollaText>
                          }
                          onPress={() =>
                            changeStatus(detailData?._id, 'completed')
                          }
                        />
                      </View>
                    ) : (
                      false
                    )}
                  </View>
                ) : (
                  false
                )}
                <View
                  style={[
                    { maxWidth: isWeb ? 400 : '100%' },
                    isWeb ? { paddingVertical: 15 } : { paddingBottom: 20 },
                  ]}>
                  <ItemDetail
                    name="Status"
                    centered
                    valueComponent={
                      <View
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}>
                        <StatusBadge status={detailData.status} />
                        <TouchableOpacity onPress={() => setShowModalLog(true)}>
                          <SociollaText
                            style={{
                              fontSize: 12,
                              color: Colors.blue0,
                              marginLeft: 20,
                              zIndex: 99,
                            }}>
                            see log
                          </SociollaText>
                        </TouchableOpacity>
                      </View>
                    }
                  />
                  {detailData?.status == 'need_approval' && (
                    <ItemDetail
                      name="Waiting Approval"
                      withoutBorder={true}
                      valueComponent={
                        <View style={{ flexDirection: 'column' }}>
                          {detailData.waiting_approvals?.length
                            ? detailData.waiting_approvals.map(
                                (appr, index) => (
                                  <User
                                    name={appr.name || ''}
                                    image={appr.image || ''}
                                    key={'list-user-approval-' + index}
                                    index={index}
                                    style={{ paddingBottom: 10 }}
                                  />
                                ),
                              )
                            : false}
                        </View>
                      }
                    />
                  )}
                  {detailData?.status == 'rejected' ? (
                    <ItemDetail
                      name="Reason"
                      centered
                      value={
                        detailData.state_histories?.length
                          ? detailData.state_histories[
                              detailData.state_histories.length - 1
                            ].reason
                          : '-'
                      }
                    />
                  ) : (
                    false
                  )}
                  <ItemDetail
                    name="DO Number"
                    value={detailData.do_number ? detailData.do_number : '-'}
                  />
                  <ItemDetail
                    name="Sales Order Number"
                    value={detailData.so_number ? detailData.so_number : '-'}
                  />
                  <ItemDetail
                    name="Customer Name"
                    value={
                      detailData.customer_name ? detailData.customer_name : '-'
                    }
                  />
                  <ItemDetail
                    name="Market Type"
                    value={detailData.channel ? detailData.channel : '-'}
                  />
                  <ItemDetail
                    name="Sales Team"
                    value={detailData.sale_team ? detailData.sale_team : '-'}
                  />
                  <ItemDetail
                    name="City"
                    value={
                      detailData?.delivery_address?.city?.name
                        ? detailData?.delivery_address?.city?.name
                        : '-'
                    }
                  />
                  <ItemDetail
                    name="Carrier"
                    value={
                      detailData.carrier_name !== 'false'
                        ? detailData.carrier_name
                        : '-'
                    }
                  />
                  <ItemDetail
                    name="Pickup Date"
                    value={
                      detailData.pick_up_date
                        ? parseDate(detailData.pick_up_date)
                        : '-'
                    }
                  />
                  <ItemDetail
                    name="GRN Number"
                    value={detailData.lpb_number ? detailData.lpb_number : '-'}
                  />
                  <ItemDetail
                    name="Shipping Date"
                    value={
                      detailData.shipping_date
                        ? parseDate(detailData.shipping_date)
                        : '-'
                    }
                  />
                  <ItemDetail
                    name="Received Date"
                    value={
                      detailData.recieved_date
                        ? parseDate(detailData.recieved_date)
                        : '-'
                    }
                  />
                  <ItemDetail
                    name="Document Received Date"
                    centered
                    value={
                      canEditDocDate ? (
                        <View style={{ width: 180 }}>
                          <DatePicker
                            hideLabel
                            name="document_received_date"
                            value={detailData.document_received_date}
                            onChange={(value) =>
                              !savingDocDate && saveDocumentReceivedDate(value)
                            }
                          />
                        </View>
                      ) : detailData.document_received_date ? (
                        parseDate(detailData.document_received_date)
                      ) : (
                        '-'
                      )
                    }
                  />
                  <ItemDetail
                    name="Notes"
                    value={detailData.notes ? detailData.notes : '-'}
                  />
                  <ItemDetail
                    name="Delivery Order Docs"
                    centered
                    value={
                      detailData.delivery_order_url ? (
                        <View
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            width: 150,
                          }}>
                          <FileInput
                            value={getFileNameFromUrl(
                              detailData.delivery_order_url,
                              'Delivery order',
                            )}
                            showTrash={false}
                            containerStyleExtra={styles.uploadForm}
                          />
                          <TouchableOpacity
                            style={{ width: 36 }}
                            onPress={() =>
                              downloadFile(
                                detailData.delivery_order_url,
                                'delivery-order',
                                '',
                                true,
                              )
                            }>
                            <Image
                              style={{
                                width: 32,
                                height: 32,
                                marginRight: 100,
                              }}
                              width={32}
                              height={32}
                              isResolveAsset
                              source={require('@/assets/img/icons/attachment.png')}
                            />
                          </TouchableOpacity>
                          {canEdit && (
                            <TouchableOpacity
                              style={{ width: 36 }}
                              onPress={() =>
                                deleteFile(detailData._id, 'delivery_order_url')
                              }>
                              <Image
                                style={{
                                  width: 20,
                                  height: 20,
                                  marginLeft: 10,
                                  marginRight: 100,
                                }}
                                width={20}
                                height={20}
                                svg={true}
                                source={require('@assets/img/icons/close-circle-blue.svg')}
                              />
                            </TouchableOpacity>
                          )}
                        </View>
                      ) : canUpload ? (
                        <Layout.FormGroup>
                          <FileInput
                            confirmRemove
                            value={getFileNameFromUrl(
                              detailData.delivery_order_url,
                              'delivery order',
                            )}
                            onChange={(value) => {
                              saveFile(detailData._id, {
                                type: 'delivery-order',
                                url: value,
                              });
                            }}
                            containerStyleExtra={styles.uploadForm}
                          />
                        </Layout.FormGroup>
                      ) : (
                        '-'
                      )
                    }
                  />
                  <ItemDetail
                    name="Proforma Invoice"
                    centered
                    value={
                      detailData?.performa_invoice ? (
                        <View
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            width: 150,
                          }}>
                          <FileInput
                            value={getFileNameFromUrl(
                              detailData.performa_invoice,
                              'Proforma invoice',
                            )}
                            showTrash={false}
                            containerStyleExtra={styles.uploadForm}
                          />
                          <TouchableOpacity
                            style={{ width: 36 }}
                            onPress={() =>
                              downloadFile(
                                detailData.performa_invoice,
                                'proforma-invoice',
                                '',
                                true,
                              )
                            }>
                            <Image
                              style={{
                                width: 32,
                                height: 32,
                                marginRight: 100,
                              }}
                              width={32}
                              height={32}
                              isResolveAsset
                              source={require('@/assets/img/icons/attachment.png')}
                            />
                          </TouchableOpacity>
                          {canEdit && (
                            <TouchableOpacity
                              style={{ width: 36 }}
                              onPress={() =>
                                deleteFile(detailData._id, 'performa_invoice')
                              }>
                              <Image
                                style={{
                                  width: 20,
                                  height: 20,
                                  marginLeft: 10,
                                  marginRight: 100,
                                }}
                                width={20}
                                height={20}
                                svg={true}
                                source={require('@assets/img/icons/close-circle-blue.svg')}
                              />
                            </TouchableOpacity>
                          )}
                        </View>
                      ) : canUpload ? (
                        <Layout.FormGroup>
                          <FileInput
                            confirmRemove
                            value={getFileNameFromUrl(
                              detailData.performa_invoice,
                              'Proforma invoice',
                            )}
                            onChange={(value) => {
                              saveFile(detailData._id, {
                                type: 'proforma-invoice',
                                url: value,
                              });
                            }}
                            containerStyleExtra={styles.uploadForm}
                          />
                        </Layout.FormGroup>
                      ) : (
                        '-'
                      )
                    }
                  />
                  {detailData?.channel?.toLowerCase() == 'mt' && (
                    <ItemDetail
                      name="Goods Received"
                      centered
                      value={
                        detailData?.goods_recieved ? (
                          <View
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              width: 150,
                            }}>
                            <FileInput
                              value={getFileNameFromUrl(
                                detailData.goods_recieved,
                                'Goods received',
                              )}
                              showTrash={false}
                              containerStyleExtra={styles.uploadForm}
                            />
                            <TouchableOpacity
                              style={{ width: 36 }}
                              onPress={() =>
                                downloadFile(
                                  detailData.goods_recieved,
                                  'goods-received',
                                  '',
                                  true,
                                )
                              }>
                              <Image
                                style={{
                                  width: 32,
                                  height: 32,
                                  marginRight: 100,
                                }}
                                width={32}
                                height={32}
                                isResolveAsset
                                source={require('@/assets/img/icons/attachment.png')}
                              />
                            </TouchableOpacity>
                            {canEdit && (
                              <TouchableOpacity
                                style={{ width: 36 }}
                                onPress={() =>
                                  deleteFile(detailData._id, 'goods_recieved')
                                }>
                                <Image
                                  style={{
                                    width: 20,
                                    height: 20,
                                    marginLeft: 10,
                                    marginRight: 100,
                                  }}
                                  width={20}
                                  height={20}
                                  svg={true}
                                  source={require('@assets/img/icons/close-circle-blue.svg')}
                                />
                              </TouchableOpacity>
                            )}
                          </View>
                        ) : canUpload ? (
                          <Layout.FormGroup>
                            <FileInput
                              confirmRemove
                              value={getFileNameFromUrl(
                                detailData.goods_recieved,
                                'Goods received',
                              )}
                              onChange={(value) => {
                                saveFile(detailData._id, {
                                  type: 'goods-received',
                                  url: value,
                                });
                              }}
                              containerStyleExtra={styles.uploadForm}
                            />
                          </Layout.FormGroup>
                        ) : (
                          '-'
                        )
                      }
                    />
                  )}
                  <ItemDetail
                    name="Other Files"
                    centered
                    value={
                      detailData.others?.length || canUpload ? (
                        <AttachmentsInput
                          testID="lpb-detail-other-files"
                          showLabel={false}
                          readOnly={!canEdit}
                          value={detailData.others || []}
                          onChange={(updatedAttachments) =>
                            setDetailData({
                              ...detailData,
                              others: updatedAttachments,
                            })
                          }
                          onUploadFile={(url, name) =>
                            saveOtherFile(detailData._id, url, name)
                          }
                          onRemoveFile={(item) =>
                            deleteOtherFile(detailData._id, item.url)
                          }
                        />
                      ) : (
                        '-'
                      )
                    }
                  />
                  <ItemDetail
                    name="Created Date"
                    value={
                      detailData.created_at
                        ? parseDate(detailData.created_at)
                        : '-'
                    }
                  />
                  <ItemDetail
                    name="Submitted Date, By"
                    value={
                      detailData.submitted_date && detailData.created_by
                        ? `${parseDate(detailData.submitted_date)}, ${
                            detailData.created_by.name
                          }`
                        : '-'
                    }
                  />
                </View>
              </View>
              {modalReject && (
                <ModalReject
                  show={modalReject}
                  onClose={() => setModalReject(false)}
                  onSubmit={changeStateToRejected}
                  title={'Reject GRN'}
                />
              )}
              {showModalLog && (
                <ModalLog
                  show={showModalLog}
                  status={detailData.status}
                  onClose={() => setShowModalLog(false)}
                  logType="lpb"
                  logLabel="GRN"
                  logId={props.route.params.id}
                  logFormatter={LPB_LOG_FORMATTER}
                  data={detailData}
                />
              )}
            </Layout.Wrapper>
            {!isWeb && canRequestConfirmation ? (
              <Layout.ButtonSticky
                route={props?.route}
                show={props?.route?.name == 'LPBDetailScreen'}
                buttons={[
                  {
                    title: 'Request Confirmation',
                    onPress: () => {
                      changeStatus(detailData?._id, 'need_approval');
                    },
                    disabled: !isAvailableSubmit(),
                  },
                ]}
              />
            ) : (
              false
            )}
            {!isWeb &&
            showCompletedButton &&
            detailData?.status == 'approved' ? (
              <Layout.ButtonSticky
                route={props?.route}
                show={props?.route?.name == 'LPBDetailScreen'}
                buttons={[
                  {
                    title: 'Completed',
                    onPress: () => {
                      changeStatus(detailData?._id, 'completed');
                    },
                  },
                ]}
              />
            ) : (
              false
            )}
            {!isWeb && canApprove() ? (
              <Layout.ButtonSticky
                route={props?.route}
                show={props?.route?.name == 'LPBDetailScreen'}
                buttons={[
                  {
                    title: 'Reject',
                    type: 'primary',
                    onPress: () => {
                      setModalReject(true);
                    },
                  },
                  {
                    title: 'Approve',
                    onPress: () => {
                      changeStatus(detailData?._id, 'approved');
                    },
                  },
                ]}
              />
            ) : (
              false
            )}
          </ScrollContainer>
        </>
      ) : (
        false
      )}
      {detailData._id && !hideComments ? (
        <>
          {isWeb ? (
            <FloatCommentButton
              onPress={() => setShowComments(true)}
              drawerVisible={showComments}
              entityType="lpb"
              entityId={detailData._id}
            />
          ) : (
            false
          )}
          <CommentDrawer
            visible={showComments}
            onClose={() => setShowComments(false)}
            entityType="lpb"
            entityId={detailData._id}
            entityRef={detailData.do_number}
          />
        </>
      ) : (
        false
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  sectionWrapper: { width: '100%', padding: 15 },
  rowContainer: { flexDirection: 'row', alignItems: 'center' },
  uploadForm: {
    borderRadius: 4,
    borderWidth: 1,
    height: isWeb ? 35 : 45,
    paddingHorizontal: 16,
    borderColor: Colors.grey0,
    width: isWeb ? '100%' : 140,
    marginRight: isWeb ? '' : 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});

export default LPBDetailScreen;
